import React, { useState, useRef } from 'react';
import { ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Lead } from '../../../types';
import { LeadPromptSegment } from '../../../utils/tableUtils';
import { normalizeSourceName, parseSourceUrl, getFaviconUrl } from '../../../utils/leadPanelUtils';
import { LinkedInIcon } from '../../common/LinkedInIcon';

interface ReasoningCriterion {
  criterion_text: string;
  reasoning_text: string;
  satisfied: 'yes' | 'no' | 'unclear';
  source_urls: (string | any)[];
}

interface MobileDatasetCardProps {
  lead: Lead;
  segments: LeadPromptSegment[];
  onClick: () => void;
  index: number;
  dataTour?: string;
}

export const MobileDatasetCard: React.FC<MobileDatasetCardProps> = ({
  lead,
  // segments available via props but not used in current implementation
  onClick,
  dataTour,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [expandedText, setExpandedText] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const data = lead.data || {};
  const reasoning = lead.reasoning || {};

  const primaryName = data.Name || data.name || Object.values(data)[0] || 'Unknown';
  const url = data.URL || data.url || data.Website || data.website || '';
  const description = data.Description || data.description || '';

  const allCriteria = Object.entries(reasoning).map(([key, value]) => ({
    key,
    criterion: value as ReasoningCriterion,
    value: data[key] || '',
  }));

  const matchedCount = allCriteria.filter(c => c.criterion.satisfied === 'yes').length;
  const totalCriteria = allCriteria.length;

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
    setActiveSlide(0);
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollLeft = scrollRef.current.scrollLeft;
      const cardWidth = scrollRef.current.offsetWidth;
      setActiveSlide(Math.round(scrollLeft / cardWidth));
    }
  };

  const scrollToSlide = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        left: index * scrollRef.current.offsetWidth,
        behavior: 'smooth',
      });
    }
  };

  const toggleTextExpand = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedText(expandedText === key ? null : key);
  };

  const displayUrl = typeof url === 'string'
    ? url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]
    : '';

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm" data-tour={dataTour}>
      <style>{`
        .snap-x { scroll-snap-type: x mandatory; }
        .snap-center { scroll-snap-align: center; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Main content - tappable to view details */}
      <button onClick={onClick} className="w-full p-4 text-left active:bg-gray-50 transition-colors">
        {/* Title row with icon - aligned */}
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-[16px] font-semibold text-gray-900 leading-tight truncate flex-1 min-w-0">
            {primaryName}
          </h3>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {url && (
              <a
                href={typeof url === 'string' && url.startsWith('http') ? url : `https://${url}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-1 rounded-lg active:bg-gray-100"
              >
                {typeof url === 'string' && url.toLowerCase().includes('linkedin') ? (
                  <LinkedInIcon size={18} />
                ) : (
                  <img
                    src={getFaviconUrl(displayUrl, 32)}
                    alt=""
                    className="w-[18px] h-[18px] rounded"
                    onError={(e: any) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                    }}
                  />
                )}
              </a>
            )}
            <ChevronRight className="w-5 h-5 text-gray-300" />
          </div>
        </div>
        {/* Description below title */}
        {description && (
          <p className="text-[13px] text-gray-500 leading-relaxed mt-1.5 line-clamp-2">
            {description}
          </p>
        )}
      </button>

      {/* Match summary - tappable to expand criteria */}
      {totalCriteria > 0 && (
        <button
          onClick={handleExpandClick}
          className="w-full flex items-center justify-between px-4 py-3 border-t border-gray-100 active:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            {/* Small status dots */}
            <div className="flex items-center gap-0.5">
              {allCriteria.slice(0, 5).map((item, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full ${
                    item.criterion.satisfied === 'yes' ? 'bg-emerald-500' : 'bg-gray-200'
                  }`}
                />
              ))}
              {totalCriteria > 5 && (
                <span className="text-[10px] text-gray-400 ml-1">+{totalCriteria - 5}</span>
              )}
            </div>
            <span className="text-[13px] text-gray-500">
              <span className="font-medium text-gray-900">{matchedCount}</span>/{totalCriteria}
            </span>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </button>
      )}

      {/* Expanded criteria carousel */}
      {isExpanded && totalCriteria > 0 && (
        <div className="border-t border-gray-100 bg-gray-50/50">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto snap-x no-scrollbar"
          >
            {allCriteria.map((item, idx) => {
              const sources = (item.criterion.source_urls || [])
                .map(parseSourceUrl)
                .filter(Boolean)
                .slice(0, 3);

              const isMatch = item.criterion.satisfied === 'yes';
              const isTextExpanded = expandedText === `text-${idx}`;
              const hasLongText = item.criterion.reasoning_text?.length > 120;

              return (
                <div key={item.key} className="flex-shrink-0 w-full snap-center p-4">
                  {/* Criterion header */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                      isMatch ? 'bg-emerald-500' : 'bg-gray-200'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">
                        Criteria {idx + 1} of {totalCriteria}
                      </p>
                      <p className="text-[14px] font-medium text-gray-900 leading-snug">
                        {item.criterion.criterion_text}
                      </p>
                    </div>
                  </div>

                  {/* Status badge - minimal */}
                  <div className="mb-3">
                    <span className={`inline-flex items-center text-[11px] font-medium ${
                      isMatch ? 'text-gray-700' : 'text-gray-400'
                    }`}>
                      {isMatch ? 'Match' : 'No match'}
                    </span>
                  </div>

                  {/* Justification */}
                  {item.criterion.reasoning_text && (
                    <div className="mb-3">
                      <p
                        className={`text-[13px] text-gray-600 leading-relaxed ${
                          !isTextExpanded && hasLongText ? 'line-clamp-3' : ''
                        }`}
                      >
                        {item.criterion.reasoning_text}
                      </p>
                      {hasLongText && (
                        <button
                          onClick={(e) => toggleTextExpand(`text-${idx}`, e)}
                          className="text-[12px] font-medium text-gray-900 mt-2 underline underline-offset-2"
                        >
                          {isTextExpanded ? 'Show less' : 'Read more'}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Sources */}
                  {sources.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200/50">
                      {sources.map((source: any, sIdx: number) => {
                        const isLI = source.hostname?.toLowerCase().includes('linkedin');
                        return (
                          <a
                            key={sIdx}
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className={`flex items-center gap-1.5 px-2 py-1 rounded-md ${
                              isLI
                                ? 'bg-[#0A66C2]/10 text-[#0A66C2]'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {isLI ? (
                              <LinkedInIcon size={14} />
                            ) : (
                              <img
                                src={getFaviconUrl(source.hostname, 32)}
                                alt=""
                                className="w-3.5 h-3.5 rounded"
                                onError={(e: any) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <span className="text-[11px] font-medium">
                              {isLI ? 'LinkedIn' : normalizeSourceName(source.hostname)}
                            </span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Slide indicators */}
          {totalCriteria > 1 && (
            <div className="flex items-center justify-center gap-1.5 pb-4">
              {allCriteria.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => scrollToSlide(idx, e)}
                  className={`transition-all duration-200 rounded-full ${
                    activeSlide === idx
                      ? 'w-5 h-1.5 bg-gray-800'
                      : 'w-1.5 h-1.5 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
